import { useEffect, useRef } from 'react';
import { buildSessionWsProtocol, resolveBackendWsBaseUrl } from '../lib/wsAuth';

/**
 * useAnalyticsWS — subscribe to `/api/ws/admin/tenant-analytics` and call
 * `onSnapshot(data)` whenever the server pushes a fresh snapshot.
 *
 * - `enabled=false` closes any existing socket (e.g. when the user moves
 *   off the "today" period — only today is server-pushed because other
 *   periods don't change in real-time).
 * - `token` is the bearer (admin JWT OR finance bearer — server accepts
 *   both on this channel for read-only analytics).
 * - On disconnect, automatically reconnects after `reconnectMs`.
 * - Returns a status ref (`.current`) with "connecting"/"open"/"closed"
 *   so callers can show a "live" badge.
 */
export default function useAnalyticsWS({ token, enabled, onSnapshot, reconnectMs = 3000 } = {}) {
  const wsRef = useRef(null);
  const statusRef = useRef('closed');
  const reconnectTimer = useRef(null);
  const manualCloseRef = useRef(false);

  useEffect(() => {
    if (!enabled || !token) {
      manualCloseRef.current = true;
      if (wsRef.current) {
        try { wsRef.current.close(); } catch { /* ignore */ }
      }
      statusRef.current = 'closed';
      return undefined;
    }

    manualCloseRef.current = false;
    const wsUrl = `${resolveBackendWsBaseUrl()}/api/ws/admin/tenant-analytics`;
    const protocol = buildSessionWsProtocol(token);

    const open = () => {
      statusRef.current = 'connecting';
      const ws = protocol ? new WebSocket(wsUrl, [protocol]) : new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        statusRef.current = 'open';
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg?.type === 'overview_snapshot' && msg.data) {
            onSnapshot(msg.data);
          }
        } catch {
          /* malformed — ignore */
        }
      };
      ws.onclose = () => {
        statusRef.current = 'closed';
        if (!manualCloseRef.current) {
          reconnectTimer.current = setTimeout(open, reconnectMs);
        }
      };
      ws.onerror = () => {
        try { ws.close(); } catch { /* ignore */ }
      };
    };
    open();

    return () => {
      manualCloseRef.current = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch { /* ignore */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, enabled]);

  return statusRef;
}
